// Token → data hydration for marksheet templates produced by MarksheetTemplateBuilderEditor.
// This module is framework-free so it can be called outside React components.

export interface MarksheetSubjectRenderData {
  subjectCode: string;
  subjectName: string;
  /** Default 75 if not available from backend. */
  theoryMax?: number;
  /** Default 25 if not available from backend. */
  practicalMax?: number;
  theoryMarks: number | null;
  practicalMarks: number | null;
  grade?: string;
  remark?: string;
}

export interface MarksheetRenderData {
  school: {
    name: string;
    code: string;
    diseCode: string;
    block?: string;
    district?: string;
    /** Full URL to the school's logo image. Rendered as a background watermark. */
    logoUrl?: string;
  };
  exam: {
    /** Raw type string from backend, e.g. "ANNUAL", "QUARTERLY". Will be formatted. */
    type: string;
    sessionYear: string;
  };
  student: {
    name: string;
    rollNo: string;
    enrollmentNo?: string;
    fatherName: string;
    motherName: string;
    /** Accepts string or Spring LocalDate array [year, month, day]. */
    dob?: string | number[];
    className: string;
    /** Base64 data URL or undefined. Replaced with <img> or a blank photo box. */
    photo?: string;
  };
  subjects: MarksheetSubjectRenderData[];
  summary: {
    percentage: number | string;
    totalGrade: string;
    result: string;
    rank?: number | string;
    failSubjects?: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function esc(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatExamType(raw: string): string {
  return raw.replace(/_/g, " ").toUpperCase();
}

function formatDob(dob: string | number[] | undefined): string {
  if (!dob) return "";
  if (Array.isArray(dob)) {
    const [year, month, day] = dob as number[];
    return `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${year}`;
  }
  return String(dob);
}

// ── Marks table HTML builder ──────────────────────────────────────────────────

function buildMarksTableHtml(subjects: MarksheetSubjectRenderData[]): string {
  if (subjects.length === 0) {
    return `<p style="text-align:center;padding:12px;">No subjects data available.</p>`;
  }

  let theoryMaxTotal = 0;
  let practicalMaxTotal = 0;
  let theoryObtTotal = 0;
  let practicalObtTotal = 0;

  const rows = subjects
    .map((s) => {
      const theoryMax = s.theoryMax ?? 75;
      const practicalMax = s.practicalMax ?? 25;
      const theoryObt = s.theoryMarks;
      const practicalObt = s.practicalMarks;
      const obtained =
        theoryObt !== null && practicalObt !== null ? theoryObt + practicalObt : null;

      theoryMaxTotal += theoryMax;
      practicalMaxTotal += practicalMax;
      if (theoryObt !== null) theoryObtTotal += theoryObt;
      if (practicalObt !== null) practicalObtTotal += practicalObt;

      return `
        <tr>
          <td class="ms-subject-cell">${esc(s.subjectCode)} ${esc(s.subjectName)}</td>
          <td>${theoryMax}</td>
          <td>${practicalMax}</td>
          <td>${theoryMax + practicalMax}</td>
          <td>${theoryObt !== null ? theoryObt : "--"}</td>
          <td>${practicalObt !== null ? practicalObt : "--"}</td>
          <td>${obtained !== null ? obtained : "--"}</td>
          <td>${esc(s.grade ?? "--")}</td>
          <td>${esc(s.remark ?? "")}</td>
        </tr>`;
    })
    .join("");

  return `
    <table class="ms-table ms-table-wide">
      <tbody>
        <tr>
          <th rowspan="2" class="ms-subject-cell">SUBJECT</th>
          <th colspan="3">MAXIMUM MARKS</th>
          <th colspan="3">MARKS OBTAINED</th>
          <th rowspan="2">GRADE</th>
          <th rowspan="2">REMARKS</th>
        </tr>
        <tr>
          <th>THEORY</th><th>INTERNAL</th><th>TOTAL</th>
          <th>THEORY</th><th>INTERNAL</th><th>TOTAL</th>
        </tr>
        ${rows}
        <tr class="ms-total-row">
          <td class="ms-subject-cell"><strong>TOTAL</strong></td>
          <td>${theoryMaxTotal}</td>
          <td>${practicalMaxTotal}</td>
          <td>${theoryMaxTotal + practicalMaxTotal}</td>
          <td>${theoryObtTotal}</td>
          <td>${practicalObtTotal}</td>
          <td>${theoryObtTotal + practicalObtTotal}</td>
          <td></td><td></td>
        </tr>
      </tbody>
    </table>`;
}

// ── Main renderer ─────────────────────────────────────────────────────────────

/**
 * Replaces all {{token}} placeholders in `html` with escaped values from `data`.
 * The `{{marksTable}}` token is replaced with generated table HTML (not escaped).
 * Returns the hydrated html and the original css unchanged.
 */
export function renderMarksheetTemplate(
  html: string,
  css: string,
  data: MarksheetRenderData
): { html: string; css: string } {
  const marksTableHtml = buildMarksTableHtml(data.subjects);
  const dobFormatted = formatDob(data.student.dob);

  const rendered = html
    // School
    .replace(/\{\{school\.name\}\}/g, esc(data.school.name))
    .replace(/\{\{school\.code\}\}/g, esc(data.school.code))
    .replace(/\{\{school\.diseCode\}\}/g, esc(data.school.diseCode))
    .replace(/\{\{school\.block\}\}/g, esc(data.school.block ?? ""))
    .replace(/\{\{school\.district\}\}/g, esc(data.school.district ?? ""))
    // School logo watermark — NOT escaped, raw img HTML (or empty if no logo)
    .replace(
      /\{\{school\.logo\}\}/g,
      data.school.logoUrl
        ? `<img src="${data.school.logoUrl}" alt="School Logo" style="width:100%;height:auto;object-fit:contain;opacity:0.18;" />`
        : ""
    )
    // Exam
    .replace(/\{\{exam\.type\}\}/g, esc(formatExamType(data.exam.type)))
    .replace(/\{\{session\.year\}\}/g, esc(data.exam.sessionYear))
    // Student
    .replace(/\{\{student\.name\}\}/g, esc(data.student.name))
    .replace(/\{\{student\.fatherName\}\}/g, esc(data.student.fatherName))
    .replace(/\{\{student\.motherName\}\}/g, esc(data.student.motherName))
    .replace(/\{\{student\.rollNo\}\}/g, esc(data.student.rollNo))
    .replace(/\{\{student\.enrollmentNo\}\}/g, esc(data.student.enrollmentNo ?? ""))
    .replace(/\{\{student\.class\}\}/g, esc(data.student.className))
    .replace(/\{\{student\.dob\}\}/g, esc(dobFormatted))
    // Summary
    .replace(/\{\{marksheet\.percentage\}\}/g, esc(data.summary.percentage))
    .replace(/\{\{marksheet\.totalGrade\}\}/g, esc(data.summary.totalGrade))
    .replace(/\{\{marksheet\.result\}\}/g, esc(data.summary.result))
    .replace(/\{\{marksheet\.rank\}\}/g, esc(data.summary.rank ?? ""))
    .replace(/\{\{marksheet\.failSubjects\}\}/g, esc(data.summary.failSubjects ?? ""))
    // Student photo — NOT escaped, raw img/div HTML
    // Inline styles are used for sizing so the photo is always 130×155 regardless
    // of what CSS version is stored in the saved template on the server.
    .replace(
      /\{\{student\.photo\}\}/g,
      data.student.photo
        ? `<img class="ms-student-photo" src="${data.student.photo}" alt="Student Photo" style="width:130px;height:155px;max-width:130px;max-height:155px;object-fit:cover;display:block;" />`
        : `<div class="ms-photo-box">PHOTO</div>`
    )
    // Marks table — NOT escaped, it's raw generated HTML
    .replace(/\{\{marksTable\}\}/g, marksTableHtml);

  return { html: rendered, css };
}

/**
 * Builds a preview-mode render data object using dummy values.
 * Used when the editor "Preview with sample data" button is pressed.
 */
export function buildPreviewRenderData(): MarksheetRenderData {
  return {
    school: {
      name: "DEMO PUBLIC SCHOOL",
      code: "SCH001",
      diseCode: "23050100101",
      block: "Block A",
      district: "Sample District",
      logoUrl: "/logo.png",
    },
    exam: { type: "ANNUAL", sessionYear: "2025-26" },
    student: {
      name: "RAHUL KUMAR SHARMA",
      rollNo: "101",
      enrollmentNo: "EN-2024-101",
      fatherName: "SURESH KUMAR SHARMA",
      motherName: "SUNITA SHARMA",
      dob: [2012, 6, 15],
      className: "Class 6-A",
    },
    subjects: [
      { subjectCode: "001", subjectName: "HINDI",          theoryMarks: 68, practicalMarks: 22, grade: "A" },
      { subjectCode: "002", subjectName: "ENGLISH",        theoryMarks: 71, practicalMarks: 20, grade: "A" },
      { subjectCode: "003", subjectName: "MATHEMATICS",    theoryMarks: 74, practicalMarks: 23, grade: "A+" },
      { subjectCode: "004", subjectName: "SCIENCE",        theoryMarks: 65, practicalMarks: 21, grade: "B+" },
      { subjectCode: "005", subjectName: "SOCIAL SCIENCE", theoryMarks: 70, practicalMarks: 19, grade: "A" },
    ],
    summary: {
      percentage: "78.40",
      totalGrade: "A",
      result: "PASS",
      rank: 3,
      failSubjects: "",
    },
  };
}
